import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { format, addDays, isBefore, isAfter, startOfDay, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    service_type: searchParams.get('servizio') || '',
    location: '',
    preferred_date: null,
    time_slot: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    budget: '',
    coupon_code: '',
    due_date: '',
    honeypot: '' // Anti-spam
  });
  const [voucherInfo, setVoucherInfo] = useState(null);
  const isMaternityService = /maternity|newborn|gravidanza|neonat/i.test(formData.service_type || '');

  const checkVoucher = async () => {
    const code = (formData.coupon_code || '').trim().toUpperCase();
    if (!code.startsWith('REGALO-')) { setVoucherInfo(null); return; }
    try {
      const res = await api.get(`/vouchers/validate/${encodeURIComponent(code)}`);
      setVoucherInfo(res.data);
    } catch (e) {
      setVoucherInfo({ valid: false, error: 'Impossibile verificare il buono' });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, availabilityRes, settingsRes] = await Promise.all([
          api.get('/public/services'),
          api.get('/public/availability'),
          api.get('/public/settings')
        ]);
        setServices(servicesRes.data);
        const preselected = searchParams.get('servizio');
        if (preselected && !servicesRes.data.some((s) => s.title === preselected)) {
          setFormData((prev) => ({ ...prev, service_type: '' }));
        }
        setAvailability(availabilityRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  // Handle Stripe checkout for deposit
  const handlePayDeposit = async (bookingId, amount) => {
    setPaymentLoading(true);
    try {
      const response = await api.post('/payments/create-checkout', {
        booking_id: bookingId,
        amount: amount,
        success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&booking_id=${bookingId}`,
        cancel_url: `${window.location.origin}/gestisci-prenotazione?code=${bookingSuccess?.booking_code}`
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL checkout non disponibile');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
      setPaymentLoading(false);
    }
  };

  // Helper per ottenere testi CMS con fallback
  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  const isDateDisabled = (date) => {
    if (!availability) return true;
    
    const today = startOfDay(new Date());
    const maxDate = addDays(today, availability.settings?.advance_booking_days || 90);
    
    // Check if date is in the past or too far in the future
    if (isBefore(date, today) || isAfter(date, maxDate)) return true;
    
    // Check if day of week is a working day (1=Mon, 7=Sun)
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    if (!availability.settings?.working_days?.includes(adjustedDay)) return true;
    
    // Check if date is blocked
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availability.blocked_dates?.includes(dateStr)) return true;
    if (availability.booked_dates?.includes(dateStr)) return true;
    
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setFormData(prev => ({ ...prev, preferred_date: date }));
    }
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.service_type) {
          toast.error(t('msg_error_service_required', 'Seleziona un servizio'));
          return false;
        }
        return true;
      case 2:
        if (!formData.preferred_date) {
          toast.error(t('msg_error_date_required', 'Seleziona una data'));
          return false;
        }
        if (!formData.time_slot) {
          toast.error(t('msg_error_time_slot_required', 'Seleziona una durata o fascia oraria'));
          return false;
        }
        if (!formData.location) {
          toast.error(t('msg_error_location_required', 'Inserisci la località'));
          return false;
        }
        return true;
      case 3:
        if (!formData.first_name || !formData.last_name) {
          toast.error(t('msg_error_name_required', 'Inserisci nome e cognome'));
          return false;
        }
        if (!formData.email) {
          toast.error(t('msg_error_email', 'Inserisci l\'email'));
          return false;
        }
        if (!formData.phone) {
          toast.error(t('msg_error_phone', 'Inserisci il telefono'));
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    // Anti-spam check
    if (formData.honeypot) {
      toast.error(t('msg_error_generic', 'Richiesta non valida'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date || null,
        preferred_date: format(formData.preferred_date, 'yyyy-MM-dd')
      };
      
      const response = await api.post('/bookings/', payload);
      setBookingSuccess(response.data);
      setStep(4);
    } catch (error) {
      console.error('Booking error:', error);
      const message = error.response?.data?.detail || t('msg_error_generic', 'Errore nell\'invio della richiesta');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="booking-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 lg:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
            {t('booking_page_label', 'Prenotazione')}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            {step === 4 ? t('booking_success_title', 'Richiesta Inviata!') : t('page_booking_title', 'Richiedi un Preventivo')}
          </h1>
        </motion.div>
      </div>

      {/* Progress Steps */}
      {step < 4 && (
        <div className="max-w-3xl mx-auto px-6 lg:px-12 mb-12">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? 'bg-foreground text-background'
                      : s < step
                      ? 'bg-foreground/20 text-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`step-indicator-${s}`}
                >
                  {s < step ? <Check size={16} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-0.5 ${s < step ? 'bg-foreground' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-4 text-sm text-muted-foreground">
            <span>{t('booking_step_service', 'Servizio')}</span>
            <span>{t('booking_step_date', 'Data')}</span>
            <span>{t('booking_step_contact', 'Contatto')}</span>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-card border border-border p-8 lg:p-12"
        >
          {/* Step 1: Service Selection */}
          {step === 1 && (
            <div data-testid="step-1">
              <h2 className="font-heading text-2xl mb-6">{t('booking_step1_title', 'Che tipo di servizio ti interessa?')}</h2>
              <div className="space-y-4">
                {services.map((service) => (
                  <label
                    key={service.id}
                    className={`block p-6 border cursor-pointer transition-colors ${
                      formData.service_type === service.title
                        ? 'border-foreground bg-muted/50'
                        : 'border-border hover:border-foreground/50'
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <input
                      type="radio"
                      name="service_type"
                      value={service.title}
                      checked={formData.service_type === service.title}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-heading text-xl mb-1">{service.title}</h3>
                        <p className="text-sm text-muted-foreground">{service.description}</p>
                      </div>
                      {service.starting_price && (
                        <span className="text-sm font-medium whitespace-nowrap ml-4">
                          {service.starting_price}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Date & Location */}
          {step === 2 && (
            <div data-testid="step-2">
              <h2 className="font-heading text-2xl mb-6">{t('booking_step2_title', 'Quando e dove?')}</h2>
              
              <div className="mb-8">
                <Label className="mb-3 block">{t('form_date', 'Data preferita')} *</Label>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={formData.preferred_date}
                    onSelect={handleDateSelect}
                    disabled={isDateDisabled}
                    locale={it}
                    className="rounded-md border"
                    data-testid="booking-calendar"
                  />
                </div>
                {formData.preferred_date && (
                  <p className="text-center mt-4 text-sm text-muted-foreground">
                    {t('booking_date_selected', 'Data selezionata')}: <span className="font-medium text-foreground">
                      {format(formData.preferred_date, 'dd MMMM yyyy', { locale: it })}
                    </span>
                  </p>
                )}
              </div>

              {/* Time Slot Selection - Always show with Full Day option */}
              <div className="mb-6">
                <Label className="mb-3 block">{t('booking_time_slot', 'Durata / Fascia oraria')} *</Label>
                
                {/* Full Day Option */}
                <div className="space-y-3">
                  <label 
                    className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${
                      formData.time_slot === 'full_day' ? 'border-foreground bg-muted/50' : 'border-border hover:border-foreground/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="time_slot_type"
                      checked={formData.time_slot === 'full_day'}
                      onChange={() => handleSelectChange('time_slot', 'full_day')}
                      className="w-4 h-4"
                    />
                    <div>
                      <span className="font-medium">{t('booking_full_day', 'Giornata Intera')}</span>
                      <p className="text-sm text-muted-foreground">{t('booking_full_day_desc', 'Blocca l\'intera giornata per il servizio')}</p>
                    </div>
                  </label>

                  {/* Time Slots - only if available */}
                  {availability?.settings?.time_slots?.length > 0 && (
                    <>
                      <div className="text-center text-sm text-muted-foreground">oppure seleziona una fascia oraria</div>
                      {availability.settings.time_slots.map((slot) => (
                        <label 
                          key={slot}
                          className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${
                            formData.time_slot === slot ? 'border-foreground bg-muted/50' : 'border-border hover:border-foreground/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="time_slot_type"
                            checked={formData.time_slot === slot}
                            onChange={() => handleSelectChange('time_slot', slot)}
                            className="w-4 h-4"
                          />
                          <span className="font-medium">{slot}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div>
                <Label className="mb-3 block">{t('booking_location', 'Località / Città')} *</Label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder={t('booking_location_placeholder', 'Es: Milano, Lago di Como...')}
                  className="input-artistic"
                  data-testid="location-input"
                />
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div data-testid="step-3">
              <h2 className="font-heading text-2xl mb-6">{t('booking_step3_title', 'I tuoi dati')}</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="mb-3 block">{t('form_name', 'Nome')} *</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder={t('form_name_placeholder', 'Il tuo nome')}
                    className="input-artistic"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label className="mb-3 block">{t('form_surname', 'Cognome')} *</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder={t('form_surname_placeholder', 'Il tuo cognome')}
                    className="input-artistic"
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_email', 'Email')} *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('form_email_placeholder', 'La tua email')}
                  className="input-artistic"
                  data-testid="email-input"
                />
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_phone', 'Telefono')} *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('form_phone_placeholder', 'Il tuo numero di telefono')}
                  className="input-artistic"
                  data-testid="phone-input"
                />
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('booking_budget', 'Budget indicativo (opzionale)')}</Label>
                <Select
                  value={formData.budget}
                  onValueChange={(value) => handleSelectChange('budget', value)}
                >
                  <SelectTrigger data-testid="budget-select">
                    <SelectValue placeholder={t('booking_budget_placeholder', 'Seleziona un range')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="< €500">Meno di €500</SelectItem>
                    <SelectItem value="€500-€1000">€500 - €1.000</SelectItem>
                    <SelectItem value="€1000-€2500">€1.000 - €2.500</SelectItem>
                    <SelectItem value="€2500-€5000">€2.500 - €5.000</SelectItem>
                    <SelectItem value="> €5000">Più di €5.000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_coupon', 'Codice Sconto')} / Buono Regalo (opzionale)</Label>
                <Input
                  name="coupon_code"
                  value={formData.coupon_code}
                  onChange={handleInputChange}
                  onBlur={checkVoucher}
                  placeholder="Es. REGALO-XXXXXX o codice sconto"
                  className="input-artistic uppercase"
                  data-testid="coupon-input"
                />
                {voucherInfo && (
                  <p className={`text-sm mt-2 ${voucherInfo.valid ? 'text-emerald-700' : 'text-red-600'}`} data-testid="voucher-info">
                    {voucherInfo.valid
                      ? `Buono regalo valido: € ${Number(voucherInfo.amount).toFixed(0)} (${voucherInfo.package})`
                      : voucherInfo.error}
                  </p>
                )}
              </div>

              {isMaternityService && (
                <div className="mb-6" data-testid="due-date-field">
                  <Label className="mb-3 block">Data prevista del parto (opzionale)</Label>
                  <Input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                    className="input-artistic"
                    data-testid="due-date-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Ti scriverò io circa tre settimane prima per organizzare insieme la sessione newborn.
                  </p>
                </div>
              )}

              <div>
                <Label className="mb-3 block">{t('booking_notes', 'Note aggiuntive (opzionale)')}</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder={t('booking_notes_placeholder', 'Descrivi il tuo progetto, richieste particolari...')}
                  className="resize-none"
                  data-testid="notes-input"
                />
              </div>

              {/* Honeypot - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleInputChange}
                className="absolute opacity-0 pointer-events-none"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && bookingSuccess && (
            <div className="text-center py-8" data-testid="step-4-success">
              <div className="w-20 h-20 bg-foreground text-background rounded-full flex items-center justify-center mx-auto mb-8">
                <Check size={40} />
              </div>
              <h2 className="font-heading text-3xl mb-4">{t('booking_thanks_title', 'Grazie per la tua richiesta!')}</h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {t('msg_booking_success', 'Ti abbiamo inviato un\'email di conferma. Ti contatteremo presto per confermare i dettagli.')}
              </p>
              
              <div className="bg-muted/50 p-6 text-left mb-6">
                <h3 className="font-medium mb-4">{t('booking_summary', 'Riepilogo')}</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t('booking_code', 'Codice prenotazione')}:</dt>
                    <dd className="font-mono font-medium">{bookingSuccess.booking_code}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t('form_service', 'Servizio')}:</dt>
                    <dd>{bookingSuccess.service_type}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t('form_date', 'Data')}:</dt>
                    <dd>{bookingSuccess.preferred_date}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t('booking_location', 'Località')}:</dt>
                    <dd>{bookingSuccess.location}</dd>
                  </div>
                  {bookingSuccess.time_slot && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">{t('booking_time_slot', 'Orario')}:</dt>
                      <dd>{bookingSuccess.time_slot === 'full_day' ? 'Giornata intera' : bookingSuccess.time_slot}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Deposit Info - Always shown when deposit > 0 */}
              {bookingSuccess.deposit_amount > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-6 text-left mb-6" data-testid="deposit-info">
                  <h3 className="font-heading text-lg mb-3 text-amber-800">
                    {t('deposit_info_title', 'Informazioni sulla Caparra')}
                  </h3>
                  <div className="space-y-2 text-sm text-amber-700">
                    <p>
                      <strong>{t('deposit_amount_label', 'Caparra richiesta')}:</strong> €{bookingSuccess.deposit_amount?.toFixed(2)} (15% del servizio)
                    </p>
                    <p>
                      {bookingSuccess.status === 'confermata' ? (
                        t('deposit_ready_message', 'La tua prenotazione è stata approvata! Procedi con il pagamento della caparra per bloccare definitivamente la data.')
                      ) : (
                        t('deposit_pending_message', 'Dopo l\'approvazione della tua richiesta, riceverai un\'email con il link per il pagamento della caparra.')
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Section - shown only if booking is confirmed and payment pending */}
              {bookingSuccess.deposit_amount > 0 && bookingSuccess.status === 'confermata' && bookingSuccess.payment_status !== 'caparra_pagata' && (
                <div className="bg-primary/5 border border-primary/20 p-6 mb-6" data-testid="payment-section">
                  <h3 className="font-heading text-xl mb-3">{t('pay_deposit_title', 'Conferma con Caparra')}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t('pay_deposit_description', 'Per confermare definitivamente la prenotazione, versa la caparra di')} <strong>€{bookingSuccess.deposit_amount?.toFixed(2)}</strong>
                  </p>
                  <Button 
                    onClick={() => handlePayDeposit(bookingSuccess.id, bookingSuccess.deposit_amount)}
                    disabled={paymentLoading}
                    className="w-full sm:w-auto"
                    data-testid="pay-deposit-btn"
                  >
                    {paymentLoading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Caricamento...
                      </>
                    ) : (
                      <>
                        {t('btn_pay_deposit', 'Paga Caparra')} - €{bookingSuccess.deposit_amount?.toFixed(2)}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">
                    Accettiamo: Carta, Apple Pay, Google Pay
                  </p>
                </div>
              )}

              {bookingSuccess.payment_status === 'caparra_pagata' && (
                <div className="bg-green-50 border border-green-200 p-4 mb-6 text-green-700">
                  ✓ Caparra pagata - Prenotazione confermata
                </div>
              )}

              <p className="text-sm text-muted-foreground mb-6">
                {t('booking_track_info', 'Puoi tracciare lo stato della tua richiesta usando il codice prenotazione e la tua email.')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => navigate('/gestisci-prenotazione?code=' + bookingSuccess.booking_code)} variant="outline" data-testid="manage-booking-btn">
                  Gestisci Prenotazione
                </Button>
                <Button onClick={() => navigate('/')} data-testid="back-home-btn">
                  {t('btn_back_home', 'Torna alla Home')}
                </Button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex justify-between mt-8 pt-8 border-t border-border">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep} data-testid="prev-step-btn">
                  <ArrowLeft size={16} className="mr-2" />
                  {t('btn_back', 'Indietro')}
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button onClick={nextStep} data-testid="next-step-btn">
                  {t('btn_next', 'Avanti')}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-booking-btn">
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      {t('btn_sending', 'Invio in corso...')}
                    </>
                  ) : (
                    <>
                      {t('btn_submit', 'Invia Richiesta')}
                      <ArrowRight size={16} className="ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
